import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import LoadingWrapper from 'components/LoadingWrapper';
import dayjs from 'dayjs';
import intl, { getInlineString } from 'i18n/intl';
import { isUndefined } from 'lodash';
import { filterOptionLowerCaseInclude } from 'pages/appSettings/allocateSettings/ViewerPage';
import { isDefined } from 'shared/typechecks';
import {
  PrefReviewLoading,
  PrefReviewSelector,
  getViewLink,
  updatePrefReview,
} from 'slices/appsPrefReviewSettings';
import { fetchFields, fieldsSelector } from 'slices/field.slice';
import { useAppDispatch } from 'slices/store';
import { numberArrayParser } from '@timeedit/registration-shared';
import type { TField } from '@timeedit/types/lib/types';
import {
  Alert,
  Button,
  DefaultContentLayout,
  ScrollableSelect,
  Typography,
} from '@timeedit/ui-components';
import { DatePicker, Select } from '@timeedit/ui-components/antd';

const language = intl.messages as Record<string, string>;

const checkBoxOptions = [
  {
    value: '',
    label: '',
  },
  {
    value: '1',
    label: language.yes,
  },
  {
    value: '0',
    label: language.no,
  },
];

function fieldName(field: TField): string {
  return `${field.name} (${field.extId} ${field.id})`;
}

function makeValidDate(addYear: number, unix?: number) {
  if (isDefined(unix) && unix > 0) {
    const date = dayjs.unix(unix);
    if (date.isValid()) {
      return date;
    }
  }
  return dayjs().startOf('year').add(addYear, 'year');
}

function validUnix(val?: dayjs.Dayjs, _default = dayjs().unix()): number {
  return isDefined(val) ? val.unix() : _default;
}

type Range = { start: number; end: number };
type FieldValues = Record<number, string[]>;
type CurrentPeriod = { range?: Range; fields?: FieldValues };

const AllocateCurrentPeriodPage = () => {
  const dispatch = useAppDispatch();
  const formatDate = 'DD/MM/YYYY';
  const allFields = useSelector(fieldsSelector);
  const isLoading = useSelector(PrefReviewLoading);
  const viewLink = useSelector(PrefReviewSelector);

  const [fieldValues, setFieldValues] = React.useState<FieldValues>({});
  const defaultRange = useRef({
    start: dayjs(`${dayjs().year()}-01-01`).unix(),
    end: dayjs(`${dayjs().year() + 2}-01-01`).unix(),
  });
  const [range, setRange] = useState<Range>(defaultRange.current);

  useEffect(() => {
    dispatch(getViewLink());
    dispatch(fetchFields());
  }, [dispatch]);
  useEffect(() => {
    if (viewLink.currentPeriod) {
      try {
        const current: CurrentPeriod = JSON.parse(viewLink.currentPeriod);
        setRange(current.range);
        setFieldValues(current.fields);
      } catch (e) {
        console.log(e);
      }
    }
  }, [viewLink]);

  const _handleSubmit = () => {
    const current: CurrentPeriod = { range, fields: fieldValues };
    dispatch(
      updatePrefReview({
        currentPeriod: JSON.stringify(current),
      })
    );
  };

  const categoryDropdowns = useMemo(() => {
    const ids = numberArrayParser(Object.keys(fieldValues));
    if (!isDefined(ids)) {
      return '';
    }
    if (ids.length === 0) {
      return '';
    }
    const dropdowns = ids
      .map((id) => {
        const field = allFields.find((f) => f.id === id);
        const key = `cat_${id}`;
        if (!isDefined(field)) {
          return undefined;
        }
        const categoryOptions = createCategoryOptions(field);
        if (isUndefined(categoryOptions)) {
          return (
            <div key={`missing${key}`}>
              {getInlineString(
                'notACategoryField',
                `${fieldName(field)}: ${field.fieldType}`
              )}
            </div>
          );
        }
        const categoryChange = (values: string[]) => {
          setFieldValues((prev) => ({ ...prev, [id]: values }));
        };
        return (
          <div key={`tip${key}`} style={{ margin: '16px 0' }}>
            <div>{fieldName(field)}</div>
            <Select
              key={key}
              mode="multiple"
              value={fieldValues[field.id]}
              style={{ minWidth: '300px' }}
              showSearch
              allowClear
              placeholder={field.name}
              onChange={categoryChange}
              filterOption={filterOptionLowerCaseInclude}
              options={categoryOptions}
              popupMatchSelectWidth={false}
            />
          </div>
        );
      })
      .filter(isDefined);
    if (dropdowns.length === 0) {
      return '';
    }
    return dropdowns;
  }, [allFields, fieldValues]);

  function createCategoryOptions(field: TField) {
    if (field.fieldType === 'CHECKBOX') {
      return checkBoxOptions;
    }
    if (!isDefined(field.categories)) {
      return undefined;
    }
    return field.categories.map((name) => ({
      value: name,
      label: name,
    }));
  }

  const fieldsOptions = useMemo(() => {
    if (allFields && allFields.length > 0) {
      return allFields
        ?.filter((field) => field.fieldType === 'CATEGORY')
        .map((field) => ({
          value: field.id,
          label: fieldName(field),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
  }, [allFields]);

  const limitreservations = (
    <div className="limitreservation-wrapper">
      <div>
        <Typography.Title level={4}>{language.reservations}</Typography.Title>
        {language.limitReservations}
        <br />
        <DatePicker
          allowClear={false}
          value={makeValidDate(0, range?.start)}
          onChange={(val) =>
            setRange((prev) => ({
              ...prev,
              start: validUnix(val, defaultRange.current.start),
            }))
          }
          format={formatDate}
        />
        {' - '}
        <DatePicker
          allowClear={false}
          value={makeValidDate(2, range?.end)}
          onChange={(val) =>
            setRange((prev) => ({
              ...prev,
              end: validUnix(val, defaultRange.current.end),
            }))
          }
          format={formatDate}
        />
      </div>
      <br />
      <Alert
        className="help-text"
        description={language.allocateCurrentPeriodReservationHelp}
        type="info"
        showIcon={false}
      />
    </div>
  );

  const limitobjects = (
    <div className="limitobjects-wrapper">
      <div>
        <Typography.Title level={4}>
          {language.courseModuleFilter}
        </Typography.Title>
        {categoryDropdowns}
        <ScrollableSelect
          title={language.addObjectCategoryToFilter}
          value={Object.keys(fieldValues)}
          options={fieldsOptions as any}
          onChange={(values) =>
            setFieldValues((fieldValues) => {
              return values.reduce((acc: FieldValues, key) => {
                return { ...acc, [key]: fieldValues[key] };
              }, {});
            })
          }
          width="100%"
          rowNum={4}
        />
      </div>
      <br />
      <Alert
        className="help-text"
        description={language.allocateCurrentPeriodFilterCategoryHelp}
        type="info"
        showIcon={false}
      />
    </div>
  );

  return (
    <DefaultContentLayout
      title={language.manage_period_page}
      subtitle={language.manage_period_help}
      actions={
        <Button type="primary" onClick={_handleSubmit}>
          {language.save_changes}
        </Button>
      }
    >
      <div className="allocate-settings-container">
        <div className="default-content-layout content-box">
          <Alert
            className="help-text"
            description={language.allocateCurrentPeriodHelp}
            type="info"
            showIcon={false}
          />
          <br />
          <LoadingWrapper isLoading={isLoading}>
            {limitreservations}
            {limitobjects}
          </LoadingWrapper>
        </div>
      </div>
    </DefaultContentLayout>
  );
};

export default AllocateCurrentPeriodPage;
